import React, { lazy, Suspense, useState } from 'react';
import './ChecklistTask.scss';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'primereact/checkbox';
import { ChecklistTask as ChecklistTaskType } from '../data/checklistData';

const ClarityDashboard = lazy(() => import('./charts/ClarityDashboard'));
const ContentTypeChart = lazy(() => import('./charts/ContentTypeChart'));
const YouTubeImpactChart = lazy(() => import('./charts/YouTubeImpactChart'));
const TrafficSourcesChart = lazy(() => import('./charts/TrafficSourcesChart'));

interface ChecklistTaskProps {
  task: ChecklistTaskType;
  isChecked: boolean;
  checkedDate: string | null;
  onToggle: () => void;
}

const ChecklistTask: React.FC<ChecklistTaskProps> = ({ task, isChecked, checkedDate, onToggle }) => {
  const { t } = useTranslation();
  const [isInstructionOpen, setIsInstructionOpen] = useState(false);

  const hasDetails = !!task.descriptionKey;
  const hasExamples = task.examples && task.examples.length > 0;
  // Presence follows the catalog (the canon); the i18n bundle only translates it.
  const instructionKey = task.titleKey.replace(/\.title$/, '.instruction');
  const hasInstruction = !!task.defaultInstruction;
  const instructionText = hasInstruction ? t(instructionKey, { defaultValue: task.defaultInstruction }) : '';

  const formattedDate = checkedDate
    ? checkedDate.split('-').reverse().join('.')
    : null;

  return (
    <div className={`checklist-task${isChecked ? ' checklist-task--checked' : ''}`}>
      <div className="checklist-task__checkbox">
        <Checkbox checked={isChecked} onChange={onToggle} />
      </div>

      <div className="checklist-task__content">
        <div className="checklist-task__title">{t(task.titleKey, { defaultValue: task.defaultTitle })}</div>

        {hasDetails && (
          <p className="checklist-task__description">
            {t(task.descriptionKey!, { defaultValue: task.defaultDescription })}
          </p>
        )}

        {hasExamples && (
          <ul className="checklist-task__examples">
            {task.examples!.map(({ key, fallback }) => (
              <li key={key}>{t(key, { defaultValue: fallback })}</li>
            ))}
          </ul>
        )}

        {hasInstruction && isInstructionOpen && (
          <div className="checklist-task__instruction">
            {instructionText}
            {task.instructionChart && (
              <div className="checklist-task__instruction-chart">
                <Suspense fallback={<div className="checklist-task__chart-placeholder" />}>
                  {task.instructionChart === 'clarity' && <ClarityDashboard />}
                  {task.instructionChart === 'contentType' && <ContentTypeChart />}
                  {task.instructionChart === 'youtubeImpact' && <YouTubeImpactChart />}
                  {task.instructionChart === 'trafficSources' && <TrafficSourcesChart />}
                </Suspense>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="checklist-task__actions">
        {isChecked && formattedDate && (
          <span className="checklist-task__date">&#10003; {formattedDate}</span>
        )}
        {hasInstruction && (
          <button
            type="button"
            className="checklist-task__instruction-toggle"
            title={t('geoChecklist.learnMore')}
            aria-expanded={isInstructionOpen}
            onClick={() => setIsInstructionOpen((prev) => !prev)}
          >
            <span
              className={`ico--bottom ico--xs checklist-task__instruction-chevron${isInstructionOpen ? ' checklist-task__instruction-chevron--open' : ''}`}
            />
          </button>
        )}
      </div>
    </div>
  );
};

export { ChecklistTask };
export default ChecklistTask;
